import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import { Trash2, Plus, Loader2, Image as ImageIcon, Upload } from 'lucide-react';
import { resolveImageUrl } from '../lib/media';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Generic manager for a list of items with an image + configurable text fields.
// props: token, onUnauthorized, resource ('picks'|'products'), fields:[{key,label,placeholder,required}]
export const AdminItemManager = ({ token, onUnauthorized, resource, fields }) => {
  const buildEmpty = useCallback(() => {
    const f = { image: '' };
    fields.forEach((fld) => { f[fld.key] = ''; });
    return f;
  }, [fields]);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(buildEmpty());
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const authFetch = useCallback((url, opts = {}) => fetch(url, {
    ...opts,
    headers: { ...(opts.headers || {}), Authorization: `Bearer ${token}` }
  }), [token]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/admin/${resource}`);
      if (res.status === 401) { onUnauthorized(); return; }
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error('Failed to load items');
    } finally {
      setLoading(false);
    }
  }, [authFetch, resource, onUnauthorized]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await authFetch(`${API}/admin/upload`, { method: 'POST', body: fd });
      if (res.status === 401) { onUnauthorized(); return; }
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Upload failed');
      setForm((prev) => ({ ...prev, image: data.url }));
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.image) { toast.error('Please upload an image'); return; }
    const missing = fields.filter((f) => f.required && !form[f.key]);
    if (missing.length) { toast.error(`Please fill in: ${missing.map((m) => m.label).join(', ')}`); return; }
    setSaving(true);
    try {
      const res = await authFetch(`${API}/admin/${resource}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (res.status === 401) { onUnauthorized(); return; }
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to add');
      setForm(buildEmpty());
      toast.success('Added');
      fetchItems();
    } catch (err) {
      toast.error('Failed to add');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await authFetch(`${API}/admin/${resource}/${id}`, { method: 'DELETE' });
      if (res.status === 401) { onUnauthorized(); return; }
      if (!res.ok) throw new Error('delete failed');
      toast.success('Removed');
      setItems((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      toast.error('Failed to remove');
    }
  };

  return (
    <div data-testid={`${resource}-manager`}>
      <Card className="mb-8 border-2 border-[#8b1538]/20">
        <CardContent className="p-6">
          <h2 className="text-xl font-bold text-[#1e1919] mb-4 flex items-center gap-2">
            <Plus className="w-5 h-5 text-[#8b1538]" /> Add New
          </h2>
          <form onSubmit={handleAdd} className="space-y-4">
            <div>
              <Label className="text-[#1e1919] font-semibold">Image</Label>
              <div className="mt-2 flex items-center gap-4">
                <label className="inline-flex items-center gap-2 px-4 py-2 rounded-md border-2 border-[#8b1538] text-[#8b1538] font-semibold cursor-pointer hover:bg-[#8b1538]/5 transition-colors">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {uploading ? 'Uploading...' : 'Choose Image'}
                  <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" onChange={handleUpload} data-testid={`${resource}-image-input`} className="hidden" disabled={uploading} />
                </label>
                {form.image && (
                  <img src={resolveImageUrl(form.image)} alt="preview" data-testid={`${resource}-image-preview`} className="w-16 h-16 object-cover rounded-md border border-[#736c64]/30" />
                )}
              </div>
              <p className="text-xs text-[#736c64] mt-1">JPG, PNG, GIF or WEBP · up to 5MB</p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {fields.map((fld) => (
                <div key={fld.key}>
                  <Label className="text-[#1e1919] font-semibold">{fld.label}{fld.required ? ' *' : ''}</Label>
                  <Input
                    value={form[fld.key]}
                    onChange={(e) => setForm({ ...form, [fld.key]: e.target.value })}
                    data-testid={`${resource}-${fld.key}-input`}
                    className="mt-2 border-[#736c64]/30 focus:border-[#8b1538]"
                    placeholder={fld.placeholder || ''}
                  />
                  {fld.hint && <p className="text-xs text-[#736c64] mt-1">{fld.hint}</p>}
                </div>
              ))}
            </div>
            <Button type="submit" disabled={saving} data-testid={`${resource}-add-btn`} className="bg-[#8b1538] hover:bg-[#6b0f2a] text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-2" /> Add</>}
            </Button>
          </form>
        </CardContent>
      </Card>

      <h2 className="text-xl font-bold text-[#1e1919] mb-4">
        Current {items.length > 0 && <span className="text-[#736c64] font-normal">({items.length})</span>}
      </h2>
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#8b1538]" /></div>
      ) : items.length === 0 ? (
        <p className="text-[#736c64] py-8 text-center">Nothing yet. Add one above.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={`${resource}-list`}>
          {items.map((item) => (
            <Card key={item.id} className="overflow-hidden border-2 border-transparent hover:border-[#8b1538]/20" data-testid={`${resource}-item-${item.id}`}>
              <div className="relative aspect-[4/3] bg-[#e8e4df]">
                {item.image ? (
                  <img src={resolveImageUrl(item.image)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="flex items-center justify-center h-full"><ImageIcon className="w-8 h-8 text-[#bbb5ae]" /></div>
                )}
              </div>
              <CardContent className="p-4">
                {fields.map((fld, i) => {
                  const val = item[fld.key];
                  if (!val) return null;
                  if (fld.key.includes('url')) {
                    return <a key={fld.key} href={val} target="_blank" rel="noopener noreferrer" data-testid={`${resource}-${fld.key}-${item.id}`} className="block text-xs text-[#3dd3ee] underline break-all mt-1">🔗 {fld.label}</a>;
                  }
                  return <p key={fld.key} className={i === 0 ? 'font-bold text-[#1e1919]' : 'text-sm text-[#736c64]'}>{val}</p>;
                })}
                <Button onClick={() => handleDelete(item.id)} data-testid={`${resource}-delete-${item.id}`} variant="outline" className="mt-3 w-full border-red-300 text-red-600 hover:bg-red-50">
                  <Trash2 className="w-4 h-4 mr-2" /> Remove
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminItemManager;
